import { TEXTOS } from '../../../lib/glossario'
import type { ConsolidacaoDoIie, ParcelaDoIie } from '../../../types/chamado'

const PERCENTUAL = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Como cada sub-indicador se chama na tela. Sem `default`: indicador novo quebra o build aqui. */
const ROTULO: Record<ParcelaDoIie['subIndicador'], string> = {
  BACKUP: 'Sucesso de backup',
  RESTORE: 'Sucesso de restauração',
  DISPONIBILIDADE: 'Disponibilidade do ambiente',
  COMUNICACAO: 'Comunicação de incidentes',
}

/**
 * O IIE aberto nos quatro sub-indicadores que o formam.
 *
 * **"IIE = 5%" não diz qual ferramenta olhar.** O contrato descreve quatro medições — backup,
 * restauração, disponibilidade e comunicação — e cobra um indicador só. Sem a lista, quem recebe o
 * desconto não sabe se o problema foi o backup ou o monitoramento, e são equipes diferentes.
 *
 * **Advertência não é conformidade.** O contrato reserva uma faixa que não desconta da nota e gera
 * registro; mostrá-la como "sem desconto" faria o mês parecer bom quando ele foi advertido.
 *
 * @param props os quatro sub-indicadores consolidados
 * @returns a lista das medições
 */
export function ParcelasDoIie({ consolidacao }: { consolidacao: ConsolidacaoDoIie }) {
  const t = TEXTOS.iie
  return (
    <div className="mt-3 space-y-3">
      {consolidacao.decidiuORedutor && consolidacao.redutorPercentual > 0 && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-inset ring-amber-200">
          {t.decidiuORedutor(ROTULO[consolidacao.decidiuORedutor as ParcelaDoIie['subIndicador']] ?? '')}
        </p>
      )}

      <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
        {consolidacao.parcelas.map((parcela) => (
          <li
            key={parcela.subIndicador}
            className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-3 py-2 text-xs"
          >
            <div className="min-w-0">
              <span className="font-medium text-slate-900">{ROTULO[parcela.subIndicador]}</span>{' '}
              <span className="tabular-nums text-slate-700">{PERCENTUAL.format(parcela.valor)}%</span>
              <div className="text-slate-500">{parcela.faixaDoTr}</div>
            </div>
            <Consequencia parcela={parcela} />
          </li>
        ))}
      </ul>

      {consolidacao.semMedicao.length > 0 && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
          {t.semMedicao(
            consolidacao.semMedicao
              .map((s) => ROTULO[s as ParcelaDoIie['subIndicador']] ?? s)
              .join(', '),
          )}
        </p>
      )}

      <p className="text-xs text-slate-500">{t.fonte(consolidacao.fonte)}</p>
    </div>
  )
}

/** O que o sub-indicador custou. Advertência não é desconto, e não pode parecer um. */
function Consequencia({ parcela }: { parcela: ParcelaDoIie }) {
  if (parcela.advertencia) {
    return (
      <span className="whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-900">
        {TEXTOS.iie.advertencia}
      </span>
    )
  }
  if (parcela.redutorPercentual === 0) {
    return <span className="whitespace-nowrap text-slate-500">{TEXTOS.iie.dentroDaMeta}</span>
  }
  return (
    <span className="whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 font-semibold text-red-900">
      −{parcela.redutorPercentual}%
    </span>
  )
}
